import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, MapPin, Clock, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { clinicInfo } from '../mockData';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', href: '#home' },
    { label: 'Services', href: '#services' },
    { label: 'About', href: '#about' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const handleBookNow = () => {
    window.open(clinicInfo.bookingUrl, '_blank', 'noopener,noreferrer');
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      {/* Top Utility Bar */}
      <div className="bg-teal-700 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between py-2 text-xs md:text-sm gap-2">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 md:gap-6">
              <a
                href={`tel:${clinicInfo.phone}`}
                className="flex items-center space-x-2 hover:text-teal-200 transition-colors"
              >
                <Phone className="w-3.5 h-3.5" />
                <span>{clinicInfo.phone}</span>
              </a>
              <div className="hidden sm:flex items-center space-x-2">
                <MapPin className="w-3.5 h-3.5" />
                <span>{clinicInfo.address}</span>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-2">
              <Clock className="w-3.5 h-3.5" />
              <span>Mon, Tue, Thu: 9am – 3pm</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-28 md:h-32 lg:h-36">
          {/* Logo */}
          <a
            href="#home"
            className="flex items-center group"
            onClick={(e) => scrollToSection(e, '#home')}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_podiatry-howick/artifacts/qxcgnx4y_Copy%20of%20Howick%20Podiatry%20Logo.svg"
              alt="Howick Podiatry"
              className="h-20 md:h-24 lg:h-28 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className="text-base font-medium text-gray-700 transition-colors duration-200 hover:text-teal-600 relative group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-teal-600 transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
            <Button
              onClick={handleBookNow}
              className="bg-teal-600 hover:bg-teal-700 text-white px-7 py-6 rounded-full transition-all duration-300 hover:shadow-xl hover:scale-105 text-base"
            >
              <Phone className="w-4 h-4 mr-2" />
              Book Now
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-teal-50 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-teal-700" />
            ) : (
              <Menu className="w-6 h-6 text-teal-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-teal-100 shadow-xl">
          <nav className="container mx-auto px-4 py-4 space-y-3">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className="block py-2 text-gray-700 hover:text-teal-600 font-medium transition-colors"
              >
                {item.label}
              </a>
            ))}
            <Button
              onClick={handleBookNow}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 rounded-full transition-all duration-300"
            >
              <Phone className="w-4 h-4 mr-2" />
              Book Now
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};
